import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const navItems = [
  { name: 'Home', path: '/' },
  { name: 'Inventory', path: '/inventory' },
  { name: 'Financing', path: '/financing' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

const steps = [
  { num: '01', title: 'Consultation', desc: 'We discuss your needs, budget, and must-have features.', icon: 'ri-chat-3-line' },
  { num: '02', title: 'Sourcing', desc: 'We scan our elite network for the top 3–5 vehicles matching your criteria.', icon: 'ri-search-line' },
  { num: '03', title: 'Vetting', desc: 'Comprehensive history reports and independent mechanical inspections.', icon: 'ri-file-list-3-line' },
  { num: '04', title: 'Acquisition', desc: 'We negotiate the best price and handle all paperwork on your behalf.', icon: 'ri-hand-coin-line' },
  { num: '05', title: 'Delivery', desc: 'Your vehicle arrives at your door, detailed and road-ready.', icon: 'ri-truck-line' },
];

export default function AboutPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Schema.org JSON-LD injection
  useEffect(() => {
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://paramountautosusa.com';
    
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          "@id": `${baseUrl}/about#webpage`,
          "url": `${baseUrl}/about`,
          "name": "About Paramount Motors - Premier Auto Brokerage",
          "description": "Learn about Paramount Motors, a premier automotive brokerage in Miami, FL. We source luxury and exotic vehicles from a global network with white-glove service.",
          "isPartOf": {
            "@id": `${baseUrl}/#website`
          },
          "about": {
            "@id": `${baseUrl}/#organization`
          }
        },
        {
          "@type": "AutoDealer",
          "@id": `${baseUrl}/#organization`,
          "name": "Paramount Motors",
          "url": baseUrl,
          "logo": "https://static.readdy.ai/image/41574e0492e980fd88e2c6f8d59234d9/ff8f99c2c3d017ecd9846c9b47aca342.png",
          "description": "Paramount Motors is a premier automotive brokerage specializing in luxury and exotic pre-owned vehicles. We operate as a private brokerage with access to a global network of licensed dealers, private collections, and exclusive wholesalers.",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Miami",
            "addressLocality": "Miami",
            "addressRegion": "FL",
            "addressCountry": "US"
          },
          "telephone": "+1-305-000-0000",
          "priceRange": "$$$$",
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "09:00",
              "closes": "18:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": "Saturday",
              "opens": "10:00",
              "closes": "16:00"
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0d0d0d]">
      <title>About Us | Paramount Autos USA – Miami, FL Auto Dealership</title>
      <meta name="description" content="Learn about Paramount Autos USA, Miami's premier auto brokerage. We source luxury and exotic pre-owned vehicles nationwide with white-glove service and transparent pricing." />
      <link rel="canonical" href="https://paramountautosusa.com/about" />

      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-[#0d0d0d]/95 backdrop-blur-md shadow-lg shadow-black/30 border-b border-[#C9A84C]/20 py-3' : 'bg-transparent py-4 sm:py-6'}`}>
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 cursor-pointer">
              <img
                src="https://static.readdy.ai/image/41574e0492e980fd88e2c6f8d59234d9/7d1f372df28459c5713c0f3e160d8f18.png"
                alt="Paramount Motors Logo"
                style={{ height: '5rem', width: 'auto' }}
                className="object-contain"
              />
            </Link>
            <div className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <Link key={item.name} to={item.path}
                  className={`text-[11px] font-medium tracking-widest uppercase transition-all cursor-pointer relative group ${item.name === 'About' ? 'text-[#C9A84C]' : 'text-white/60 hover:text-[#C9A84C]'}`}
                  style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {item.name}
                  {item.name === 'About' && <span className="absolute -bottom-1 left-0 w-full h-px bg-[#C9A84C]"></span>}
                </Link>
              ))}
            </div>
            <Link to="/financing" className="hidden lg:block px-6 py-2.5 rounded-sm text-xs font-bold tracking-widest uppercase bg-[#C9A84C] text-[#0d0d0d] hover:bg-[#e0bc5e] transition-all cursor-pointer whitespace-nowrap" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Financing
            </Link>
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-white">
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
          {isMobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4">
              <div className="flex flex-col gap-4">
                {navItems.map((item) => (
                  <Link key={item.name} to={item.path} onClick={() => setIsMobileMenuOpen(false)}
                    className="text-xs font-medium tracking-widest uppercase text-white/70 hover:text-[#C9A84C] transition-all cursor-pointer"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}>{item.name}</Link>
                ))}
                <Link to="/financing" onClick={() => setIsMobileMenuOpen(false)}
                  className="mt-1 px-6 py-3 rounded-sm text-xs font-bold tracking-widest uppercase bg-[#C9A84C] text-[#0d0d0d] cursor-pointer whitespace-nowrap text-center"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}>Financing</Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-[65vh] sm:h-[70vh] lg:h-[78vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a441808a1bccd94450aad25c23656cf9.jpg"
            alt="Paramount Motors — The New Standard in Automotive Acquisition"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d]/90 via-[#0d0d0d]/55 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-black/30"></div>
        </div>
        <div className="relative z-10 px-4 sm:px-8 lg:px-16 pb-8 sm:pb-14 lg:pb-20 w-full pt-20 sm:pt-24 lg:pt-28">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px w-8 bg-[#C9A84C]"></div>
            <span className="text-[#C9A84C] text-[10px] tracking-[0.35em] uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Our Story</span>
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white mb-3 leading-tight max-w-2xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            The New Standard<br />in <span className="text-[#C9A84C]">Automotive Acquisition</span>
          </h1>
          <p className="hidden sm:block text-white/50 text-xs sm:text-sm leading-relaxed max-w-lg" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
            We didn't just want to build another dealership. We built a private brokerage designed to put the power back in the hands of the driver.
          </p>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-10 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-16 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto">

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center mb-12 sm:mb-16 lg:mb-24">
            <div className="hidden lg:block relative">
              <div className="relative w-full h-[420px] rounded-sm overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20automotive%20consultant%20reviewing%20digital%20tablet%20with%20client%20in%20modern%20minimalist%20office%20space%20dark%20sophisticated%20interior%20gold%20accent%20lighting%20premium%20brokerage%20service%20consultation%20meeting&width=800&height=600&seq=pm_philosophy001&orientation=landscape"
                  alt="Paramount Motors Philosophy — Unlimited Access"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>
              <div className="absolute -bottom-4 -right-4 w-full h-full border border-[#C9A84C]/30 rounded-sm pointer-events-none"></div>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-8 bg-[#C9A84C]"></div>
                <span className="text-[#C9A84C] text-[10px] tracking-[0.35em] uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Our Philosophy</span>
              </div>
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Unlimited Access.<br /><span className="text-[#C9A84C]">Zero Compromise.</span>
              </h2>
              <p className="text-white/55 mb-4 leading-relaxed text-sm" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
                At Paramount Motors, we believe the traditional car-buying model is broken — the pressure of the showroom, the limited selection of a single lot, and the lack of transparency.
              </p>
              <p className="text-white/55 leading-relaxed text-sm" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
                <strong className="text-white/80">Paramount Motors works for you, not the inventory.</strong> By operating as a premier brokerage, we tap a global network of licensed dealers, private collections, and exclusive wholesalers. If the car exists, we will find it, vet it, and negotiate it on your behalf.
              </p>
            </div>
          </div>

          {/* The Paramount Process */}
          <div className="mb-12 sm:mb-16 lg:mb-24">
            <div className="text-center mb-8 sm:mb-12">
              <div className="flex items-center justify-center gap-3 mb-3">
                <div className="h-px w-8 bg-[#C9A84C]"></div>
                <span className="text-[#C9A84C] text-[10px] tracking-[0.35em] uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>How It Works</span>
                <div className="h-px w-8 bg-[#C9A84C]"></div>
              </div>
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                The Paramount <span className="text-[#C9A84C]">Process</span>
              </h2>
            </div>

            <div className="flex flex-col sm:grid sm:grid-cols-5 gap-4 sm:gap-5">
              {steps.map((step, i) => (
                <div key={i} className="relative flex sm:flex-col items-start sm:items-stretch gap-4 sm:gap-0 bg-[#111111] border border-white/5 rounded-sm p-4 sm:p-5">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center border border-[#C9A84C]/30 rounded-sm sm:mb-3">
                    <i className={`${step.icon} text-lg text-[#C9A84C]`}></i>
                  </div>
                  <div>
                    <div className="text-[#C9A84C]/30 text-2xl font-bold leading-none mb-1 hidden sm:block" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{step.num}</div>
                    <h4 className="text-sm sm:text-base font-bold text-white mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{step.title}</h4>
                    <p className="text-white/70 text-sm leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>{step.desc}</p>
                  </div>
                  {i < 4 && (
                    <div className="hidden sm:block absolute top-1/2 -right-3 w-6 h-px bg-[#C9A84C]/20 z-10"></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Experience Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-8 bg-[#C9A84C]"></div>
                <span className="text-[#C9A84C] text-[10px] tracking-[0.35em] uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>The Difference</span>
              </div>
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Experience the<br /><span className="text-[#C9A84C]">Paramount Difference</span>
              </h2>
              <p className="text-white/55 mb-6 leading-relaxed text-sm" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
                Paramount Motors is more than a car-buying service — it is a bespoke automotive experience. Whether you're looking for a daily luxury driver or a rare addition to your collection, we ensure the process is as refined as the vehicle itself.
              </p>
              <Link to="/inventory"
                className="inline-flex items-center gap-2 bg-[#C9A84C] hover:bg-[#e0bc5e] text-[#0d0d0d] px-6 py-3 rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-105 transform"
                style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Start Your Search Today
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="hidden lg:block relative">
              <div className="relative w-full h-[420px] rounded-sm overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=luxury%20vehicle%20being%20unloaded%20from%20enclosed%20car%20transport%20trailer%20at%20modern%20home%20driveway%20professional%20white%20glove%20delivery%20service%20pristine%20exotic%20car%20cinematic%20lighting%20premium%20automotive%20concierge&width=800&height=600&seq=pm_delivery001&orientation=landscape"
                  alt="Paramount Motors — White Glove Delivery"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>
              <div className="absolute -bottom-4 -left-4 w-full h-full border border-[#C9A84C]/30 rounded-sm pointer-events-none"></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=elegant%20modern%20garage%20interior%20with%20luxury%20exotic%20car%20pristine%20clean%20space%20dramatic%20moody%20lighting%20sophisticated%20automotive%20collection%20premium%20vehicle%20storage%20cinematic%20atmosphere&width=1920&height=600&seq=pm_about_cta_v2&orientation=landscape"
            alt="Paramount Motors"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0d0d0d]/85"></div>
        </div>
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="h-px w-8 bg-[#C9A84C]"></div>
            <span className="text-[#C9A84C] text-[10px] tracking-[0.35em] uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Get Started</span>
            <div className="h-px w-8 bg-[#C9A84C]"></div>
          </div>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Ready to Experience<br /><span className="text-[#C9A84C]">The Paramount Difference?</span>
          </h2>
          <p className="text-white/50 text-xs sm:text-sm mb-7 leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
            Let us find your perfect vehicle from our global network of premium sources.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/inventory" className="px-8 py-3 bg-[#C9A84C] hover:bg-[#e0bc5e] text-[#0d0d0d] rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-105 transform" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Browse Inventory
            </Link>
            <Link to="/contact" className="px-8 py-3 border border-[#C9A84C]/40 hover:border-[#C9A84C] text-[#C9A84C] hover:bg-[#C9A84C]/10 rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-105 transform" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#080808] border-t border-white/5 py-8 px-4 sm:px-8 lg:px-16">
        <div className="h-px bg-gradient-to-r from-transparent via-[#C9A84C] to-transparent mb-8"></div>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img
              src="https://static.readdy.ai/image/41574e0492e980fd88e2c6f8d59234d9/ff8f99c2c3d017ecd9846c9b47aca342.png"
              alt="Paramount Motors Logo"
              style={{ height: '3rem', width: 'auto' }}
              className="object-contain"
            />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            {navItems.map((item) => (
              <Link key={item.name} to={item.path} className="text-xs text-white/40 hover:text-[#C9A84C] transition-colors cursor-pointer tracking-widest uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>{item.name}</Link>
            ))}
          </div>
          <p className="text-xs text-white/20" style={{ fontFamily: 'Montserrat, sans-serif' }}>© 2025 Paramount Motors</p>
        </div>
      </footer>
    </div>
  );
}
